import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Area, AreaLocation } from '../../resources/area/area.interface';
import { BuildingTemplates } from '../../resources/building-template/building-template.interface';
import { Building } from '../../resources/building/building.interface';
import { Cluster, ClusterDragDropLocation } from '../../resources/cluster/cluster.interface';
import { ProjectElement, ProjectElementResponse } from '../../resources/element/project-element.interface';
import { ProjectModule } from '../../resources/module/project-module.interface';
import { ProjectTemplates } from '../../resources/project-template/project-template.interface';
import { Project } from '../../resources/project/project.interface';
import { ProjectService } from '../../resources/project/project.service';
import { ProjectSubmodule } from '../../resources/submodule/project-submodule.interface';
import { ResponseStatus } from '../api.interface';
import { ApiLegacyCoreService } from './api-legacy-core.service';

/**
 * Service specifically for legacy API calls.
 *
 * This file will slowly deprecate over time.
 *
 * @note Make sure to mark each method as deprecated, so there's a clear list
 *  of all the legacy calls. Each deprecated call *must* have a V2 counterpart.
 *
 * @deprecated
 */
@Injectable({
  providedIn: 'root'
})
export class ApiLegacyService extends ApiLegacyCoreService {

  constructor(
    protected httpClient: HttpClient,
    protected projectService: ProjectService
  ) {
    super(httpClient, projectService);
  }

  /**
   * Create new element.
   *
   * @deprecated
   *
   * @param element The element to add. Can either contain all properties or only the mandatory.
   * @returns The added/generated element returned by the API.
   */
  public createElement(element: Partial<ProjectElement>): Observable<{ element: Partial<ProjectElement> }> {
    const params = [
      ['element']
    ];
    return this._post<Partial<ProjectElement>>(params, {
      id: this.projectService.getId(),
      versionId: this.projectService.getVersionId(),
      element: {
        ...element
      }
    });
  }

  /**
   * Create new module.
   *
   * @deprecated
   *
   * @param module The module to add. Can either contain all properties or only the mandatory.
   * @returns The added/generated module returned by the API.
   */
  public createModule(module: Partial<ProjectModule>): Observable<any> {
    const params = [
      ['module']
    ];
    return this._post<Partial<ProjectModule>>(params, {
      id: this.projectService.getId(),
      versionId: this.projectService.getVersionId(),
      module: {
        ...module
      }
    });
  }

  /**
   * Create new submodule.
   *
   * @deprecated
   *
   * @param submodule The submodule to add. Can either contain all properties or only the mandatory.
   * @returns The added/generated submodule returned by the API.
   */
  public createSubmodule(submodule: Partial<ProjectSubmodule>): Observable<ProjectSubmodule> {
    const params = [
      ['module', submodule.moduleId],
      ['submodule']
    ];
    return this._post<Partial<ProjectSubmodule>>(params, {
      id: this.projectService.getId(),
      versionId: this.projectService.getVersionId(),
      submodule: {
        ...submodule
      }
    });
  }

  /**
   * Delete module.
   *
   * @deprecated
   *
   * @param submodule The submodule to delete.
   * @returns API response.
   */
  public deleteModule(module: ProjectModule): Observable<any> {
    const params = [
      ['module', module._id]
    ];
    return this._delete<ProjectModule>(params, {
      id: this.projectService.getId(),
      versionId: this.projectService.getVersionId(),
      module: {
        ...module
      }
    });
  }

  /**
   * Delete submodule.
   *
   * @deprecated
   *
   * @param submodule The submodule to delete.
   * @returns API response.
   */
  public deleteSubmodule(submodule: ProjectSubmodule): Observable<any> {
    const params = [
      ['module', submodule.moduleId],
      ['submodule', submodule._id]
    ];
    return this._delete<ProjectSubmodule>(params, {
      id: this.projectService.getId(),
      versionId: this.projectService.getVersionId(),
      submodule: {
        ...submodule
      }
    });
  }

  /**
   * Get building templates.
   *
   * @todo Create store for building templates. Currently this is hard to
   *  do without the Angular Router, because the data needs to be resolved.
   *  - Can be loaded in projectDataService maybe?
   * @todo Interface for Building templates.
   *
   * @deprecated
   *
   * @returns Building templates.
   */
  public getBuildingTemplates(): Observable<BuildingTemplates> {
    const params = [
      ['buildingTemplate']
    ];
    return this._get<BuildingTemplates>(params, false);
  }

  /**
   * Get project templates.
   *
   * @todo Create store for project templates. Currently this is hard to
   *  do without the Angular Router, because the data needs to be resolved.
   *  - Can be loaded in projectDataService maybe?
   * @todo Interface for Project templates.
   *
   * @deprecated
   *
   * @returns Project templates.
   */
  public getProjectTemplates(): Observable<ProjectTemplates> {
    const params = [
      ['pveTemplate']
    ];
    return this._get<ProjectTemplates>(params, false);
  }

  /**
   * Get template elements.
   *
   * Returns a list with all elements for a given project level.
   *
   * @todo Add API support for query params. Works fine right now, but
   *  feels a little hacky.
   *
   * @deprecated
   *
   * @returns Template elements for project level.
   */
  public getTemplateElements(projectLevel: ProjectElement['projectLevel']): Observable<ProjectElement[]> {
    const params = [
      [`element?projectLevel=${ projectLevel }`],
    ];
    return this._get<ProjectElement[]>(params, false);
  }

  /**
   * Get template modules.
   *
   * Returns a list with *all* template modules.
   *
   * @todo It would be better if this call would have support for template id,
   *  so the filtered tree is returned. Now this has to be done front-end.
   *
   * @deprecated
   *
   * @returns Template modules.
   */
  public getTemplateModules(): Observable<ProjectModule[]> {
    const params = [
      ['modules']
    ];
    return this._get<ProjectModule[]>(params, false);
  }

  /**
   * Move area.
   *
   * Moves area position within a cluster.
   *
   * @deprecated
   *
   * @param area The area to move.
   * @param current The current cluster and index of the area.
   * @param to The new cluster and index of the area.
   * @returns Response (whole project) as observable.
   */
  public moveArea(area: Area, from: AreaLocation, to: AreaLocation): Observable<Project> {
    const params = [
      ['building', area.buildingId],
      ['area', 'move']
    ];
    return this._put<Project>(params, {
      area: area,
      _id: this.projectService.getId(),
      paramId: area.buildingId,
      versionId: this.projectService.getVersionId(),
      srcIndex: from.index,
      targetIndex: to.index,
      clusterId: from.id,
      targetClusterId: to.id,
    });
  }

  /**
   * Move cluster.
   *
   * @deprecated
   *
   * @param cluster The cluster to move.
   * @param target Target location of the cluster after dropping.
   * @returns Response (status) as Observable.
   */
  public moveCluster(cluster: Cluster, target: ClusterDragDropLocation): Observable<ResponseStatus> {
    const params = [
      ['building', cluster.buildingId],
      ['cluster', 'move']
    ];
    return this._put<ResponseStatus>(params, {
      _id: this.projectService.getId(),
      id: cluster._id,
      buildingId: cluster.buildingId,
      paramId: cluster.buildingId,
      projectId: this.projectService.getId(),
      sourceIndex: target.from,
      target: {
        id: target.id,
        position: target.position
      },
      targetIndex: target.to,
      versionId: this.projectService.getVersionId()
    });
  }

  /**
   * Patch cluster.
   *
   * @deprecated
   *
   * @param cluster The updated cluster.
   * @param values Updated values to patch.
   * @returns Http Observable.
   */
  public patchClusterLegacy(cluster: Cluster, values: Partial<Cluster>): Observable<Cluster> {
    const params = [
      ['building', cluster.buildingId],
      ['cluster']
    ];
    return this._put<Cluster>(params, {
      _id: this.projectService.getId(),
      versionId: this.projectService.getVersionId(),
      paramId: cluster.buildingId,
      cluster: {
        clusterId: cluster._id,
        ...values
      },
    });
  }

  /**
   * Patch element.
   *
   * @deprecated
   *
   * @param element The element (unpatched).
   * @param values Updated values to patch.
   * @returns Http Observable.
   */
  public patchElement(element: ProjectElement, values: Partial<ProjectElement>): Observable<ProjectElementResponse> {
    const params = [
      ['element']
    ];
    return this._patch<ProjectElement>(params, {
      _id: this.projectService.getId(),
      versionId: this.projectService.getVersionId(),
      element: {
        elementId: element._id,
        moduleId: element.moduleId,
        submoduleId: element.submoduleId,
        constructionId: element.constructionId,
        ...values
      },
    });
  }

  /**
   * Patch module.
   *
   * @deprecated
   *
   * @param module The module (unpatched).
   * @param values Updated values to patch.
   * @returns Http Observable.
   */
  public patchModule(module: ProjectModule, values: Partial<ProjectModule>): Observable<ProjectModule> {
    const params = [
      ['module', module._id]
    ];

    values = this._patchModuleOrder(values);

    return this._patch<ProjectModule>(params, {
      versionId: this.projectService.getVersionId(),
      module: {
        _id: module._id,
        projectId: this.projectService.getId(),
        versionId: this.projectService.getVersionId(),
        ...values
      },
    });
  }

  /**
   * Patch submodule.
   *
   * @todo The orderElements call breaks because of `order: number` property. Why?
   *
   * @deprecated
   *
   * @param submodule The submodule (unpatched).
   * @param values Updated values to patch.
   * @returns Http Observable.
   */
  public patchSubmodule(submodule: ProjectSubmodule, values: Partial<ProjectSubmodule>): Observable<ProjectSubmodule> {
    const params = [
      ['module', submodule.moduleId],
      ['submodule', submodule._id]
    ];
    return this._patch<ProjectModule>(params, {
      versionId: this.projectService.getVersionId(),
      submodule: {
        _id: submodule._id,
        moduleId: submodule.moduleId,
        projectId: this.projectService.getId(),
        versionId: this.projectService.getVersionId(),
        ...values
      },
    });
  }

  /**
   * Fix for module order.
   *
   * The server expects the order to be increased by one.
   *
   * @todo Fix this back-end and remove this patch.
   *
   * @deprecated
   *
   * @param values The values to check/patch.
   * @returns The patched values.
   */
  private _patchModuleOrder(values: Partial<ProjectModule>): Partial<ProjectModule> {
    values = Object.assign({}, values);
    if (values.order !== undefined) {
      values.order++;
    }
    return values;
  }
}
